<template>
  <div class="p-users-access-projects">
    <v-row>
      <v-col md="3">
        <p-search-input
          v-model="search"
          :items="searchSuggestions"
          @submit="searchProjects"
        ></p-search-input>
      </v-col>
    </v-row>

    <v-row v-if="projects.length > 0">
      <v-col>
        <v-data-table
          :headers="tableHeaders"
          :items="projectsComputed"
          :page.sync="page"
          :items-per-page="itemsPerPage"
          :server-items-length="totalCount"
          class="p-no-borders"
          hide-default-footer
          @page-count="pageCount = $event"
          @update:page="reloadProjects"
        >
          <template v-slot:[`item.title`]="{ item }">
            <span class="font-weight-medium">{{ item.title }}</span>
          </template>
          <template v-slot:[`item.managers`]="{ item }">
            <p-user-item
              v-for="userId in item.managers"
              :key="userId"
              :value="userById(userId) || {}"
              avatar-class="mr-2"
              avatar-only
              you
            ></p-user-item>
          </template>
          <template v-slot:[`item.labels`]="{ item }">
            <div class="p-chips-wrapper">
              <v-chip
                v-for="labelId of item.labels"
                :key="labelId"
                small
                outlined
                label
              >
                {{ (projectLabelById(labelId) || {}).name }}
              </v-chip>
            </div>
          </template>
          <template v-slot:[`item.statusId`]="{ item }">
            <p-project-status :value="item.statusId"></p-project-status>
          </template>
          <template v-slot:[`item.scheme`]="{ item }">
            <v-autocomplete
              v-model="item.editedSchemeId"
              :items="schemes"
              item-value="id"
              item-text="name"
              hide-details
              clearable
              outlined
              dense
              :disabled="!hasAccess()"
              @change="setProjectScheme_(item)"
            >
            </v-autocomplete>
          </template>
        </v-data-table>

        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="15"
          class="mt-5"
        ></v-pagination>
      </v-col>
    </v-row>

    <v-row v-else-if="projectsLoaded && !search">
      <v-col>
        <v-divider class="mb-16"></v-divider>
        <p-no-data
          :text="$t('project.noProjects')"
          img="project.png"
          hide-btn
        ></p-no-data>
      </v-col>
    </v-row>
  </div>
</template>


<script>
import { mapState, mapGetters, mapActions } from 'vuex'

import PProjectStatus from '@/components/PProjectStatus'
import PSearchInput from '@/components/PSearchInput'
import PNoData from '@/components/PNoData'
import PUserItem from '@/components/PUserItem'

import snackbarMixin from '@/mixins/snackbar.mixin'
import pageMixin from '@/mixins/page.mixin'
import accessMixin from '@/mixins/access.mixin'


export default {
  components: {
    PProjectStatus,
    PSearchInput,
    PNoData,
    PUserItem
  },


  mixins: [
    snackbarMixin,
    pageMixin,
    accessMixin
  ],


  data () {
    return {
      search: '',
      page: 1,
      pageCount: 0,
      itemsPerPage: 20,
      projectsLoaded: false
    }
  },


  computed: {
    ...mapState('project', ['totalCount']),
    ...mapGetters('project', ['projects', 'searchSuggestions']),
    ...mapGetters('projectLabel', ['projectLabelById']),
    ...mapGetters('scheme', ['schemes']),
    ...mapGetters('user', ['userById']),

    projectsComputed () {
      return this.projects.map(project => {
        return {
          ...project,
          editedSchemeId: project.schemeId ? project.schemeId : null
        }
      })
    },

    tableHeaders () {
      return [
        { text: this.$t('project.table.title'), value: 'title', sortable: false },
        { text: this.$t('project.table.labels'), value: 'labels', sortable: false },
        { text: this.$t('project.table.managers'), value: 'managers', sortable: false, filterable: false },
        { text: this.$t('project.table.status'), value: 'statusId', sortable: false, filterable: false },
        { text: this.$t('project.table.scheme'), value: 'scheme', sortable: false, filterable: false }
      ]
    }
  },


  methods: {
    ...mapActions('project', ['loadProjects', 'setProjectScheme']),
    ...mapActions('projectLabel', ['loadProjectLabels']),
    ...mapActions('scheme', ['loadSchemes']),

    async searchProjects (value) {
      await this.reloadProjects(1, value)
    },

    async reloadProjects (page, search) {
      try {
        this.startPageLoading()
        this.page = page || this.page
        await this.loadProjects({
          skip: (this.page - 1) * this.itemsPerPage,
          take: this.itemsPerPage,
          search: search || this.search
        })
      } catch (error) {
        this.showSnackbarError()
        this.$log.error(error)
      } finally {
        this.stopPageLoading()
      }
    },

    async setProjectScheme_ (project) {
      try {
        this.showSnackbarProcessing()
        await this.setProjectScheme({ schemeId: project.editedSchemeId, projectId: project.id })
        this.showSnackbarSuccess(this.$t('project.schemeUpdated'))
      } catch (error) {
        project.editedSchemeId = project.scheme ? project.scheme.id : null
        this.showSnackbarError()
        this.$log.error(error)
      }
    }
  },


  async created () {
    try {
      this.startPageLoading()
      await this.loadSchemes()
      await this.loadProjectLabels()
      await this.loadProjects({ take: this.itemsPerPage })
      this.projectsLoaded = true
    } catch (error) {
      this.showSnackbarError()
      this.$log.error(error)
    } finally {
      this.stopPageLoading()
    }
  }
}
</script>
